import React, { useState, useEffect } from 'react';
import { 
  HelpCircle, Search, ChevronDown, Zap, CreditCard, Settings,
  Shield, Phone, MessageCircle, Sun, Battery, Wrench, FileText,
  Calculator, Home, Building2, Leaf, Clock
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const FAQPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('general');
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const categories = [
    { id: 'general', name: 'General', icon: HelpCircle },
    { id: 'instalacion', name: 'Instalación', icon: Wrench },
    { id: 'funcionamiento', name: 'Funcionamiento', icon: Zap },
    { id: 'financiamiento', name: 'Financiamiento', icon: CreditCard },
    { id: 'mantenimiento', name: 'Mantenimiento', icon: Settings },
    { id: 'garantias', name: 'Garantías', icon: Shield }
  ];

  const faqs = {
    general: [
      {
        question: '¿Qué es un sistema de energía solar fotovoltaica?',
        answer: 'Un sistema solar fotovoltaico convierte la luz del sol en electricidad utilizando paneles solares. Esta electricidad puede usarse para alimentar tu hogar o negocio, reduciendo significativamente tu factura de ANDE. El sistema puede estar conectado a la red eléctrica (On-Grid) o ser independiente con baterías (Off-Grid).'
      },
      {
        question: '¿Cuánto puedo ahorrar con paneles solares?',
        answer: 'El ahorro depende de tu consumo actual y el tamaño del sistema. En promedio, nuestros clientes ahorran entre 85% y 100% de su factura eléctrica. Un hogar típico que paga Gs. 500,000 mensuales puede reducir su factura a menos de Gs. 75,000. Usá nuestra calculadora solar para obtener un estimado personalizado.'
      },
      {
        question: '¿Paraguay es buen lugar para energía solar?',
        answer: '¡Excelente! Paraguay tiene una radiación solar promedio de 5.5 kWh/m²/día, superior a países como Alemania o España que son líderes en energía solar. Tenemos más de 300 días de sol al año, lo que hace que la inversión en paneles solares sea muy rentable.'
      },
      {
        question: '¿Cuál es la vida útil de un sistema solar?',
        answer: 'Los paneles solares tienen una vida útil de 25-30 años con garantía de rendimiento. Los inversores duran entre 10-15 años. Durante este tiempo, los paneles mantienen al menos el 80% de su eficiencia original. Es una inversión a largo plazo con retorno garantizado.'
      },
      {
        question: '¿Puedo vender mi excedente de energía a ANDE?',
        answer: 'Sí, mediante el sistema de Net Metering (medición neta). Cuando generás más energía de la que consumís, el excedente se inyecta a la red y se descuenta de tu factura. Si al final del mes tenés crédito, se acumula para meses siguientes. Zenex se encarga de todos los trámites con ANDE.'
      }
    ],
    instalacion: [
      {
        question: '¿Cuánto tiempo demora la instalación?',
        answer: 'Para sistemas residenciales, la instalación completa demora entre 1 y 3 días. Para sistemas comerciales o industriales puede tomar de 1 a 2 semanas dependiendo del tamaño. El proceso incluye: evaluación inicial (1-2 días), diseño y aprobación (2-3 días), instalación física, configuración y pruebas.'
      },
      {
        question: '¿Necesito hacer modificaciones en mi casa?',
        answer: 'Generalmente no. Los paneles se instalan sobre el techo existente usando estructuras de aluminio que no dañan las tejas. Solo necesitamos acceso al tablero eléctrico para la conexión. En algunos casos puede requerirse reforzar la estructura del techo, lo cual evaluamos en la visita técnica inicial.'
      },
      {
        question: '¿Qué orientación debe tener mi techo?',
        answer: 'En Paraguay, la orientación ideal es hacia el Norte para maximizar la captación solar. Sin embargo, techos con orientación Este u Oeste también funcionan muy bien (85-90% de eficiencia). Incluso techos planos son excelentes ya que podemos inclinar los paneles a 15-20° hacia el Norte.'
      },
      {
        question: '¿Qué pasa si mi techo está en sombra?',
        answer: 'Las sombras reducen significativamente la producción. Evaluamos las sombras durante la visita técnica usando herramientas especializadas. Si hay árboles o edificios que generan sombra, podemos recomendar podas o ubicaciones alternativas. Los micro-inversores y optimizadores ayudan a minimizar el impacto de sombras parciales.'
      },
      {
        question: '¿Necesito permiso municipal para instalar paneles?',
        answer: 'Para sistemas residenciales pequeños (hasta 10kW) generalmente no se requiere permiso municipal. Para sistemas más grandes o instalaciones comerciales puede requerirse. Zenex gestiona todos los permisos necesarios como parte del servicio de instalación.'
      }
    ],
    funcionamiento: [
      {
        question: '¿Los paneles funcionan en días nublados?',
        answer: 'Sí, los paneles generan electricidad incluso con cielo nublado, aunque a menor capacidad (10-25% de la producción normal). La radiación difusa sigue llegando a los paneles. En Paraguay, con nuestro clima mayormente soleado, esto no afecta significativamente la producción anual.'
      },
      {
        question: '¿Qué pasa durante la noche?',
        answer: 'Durante la noche los paneles no generan electricidad. Si estás conectado a la red (On-Grid), simplemente consumís de ANDE como siempre. Si tenés sistema con baterías (Off-Grid o Híbrido), las baterías alimentan tu hogar durante la noche con la energía almacenada durante el día.'
      },
      {
        question: '¿Qué pasa si hay un corte de luz de ANDE?',
        answer: 'En sistemas On-Grid estándar, el sistema se apaga por seguridad (para no electrocutar a técnicos de ANDE). Si querés tener energía durante cortes, necesitás un sistema híbrido con baterías o un inversor con función de respaldo. Consultá sobre nuestras soluciones de respaldo energético.'
      },
      {
        question: '¿Puedo monitorear mi sistema remotamente?',
        answer: 'Sí, todos nuestros sistemas incluyen monitoreo remoto mediante app móvil y web. Podés ver en tiempo real: generación actual, historial de producción, ahorro acumulado, estado del sistema y alertas. Nuestro equipo técnico también monitorea tu sistema para detectar cualquier anomalía.'
      },
      {
        question: '¿Cuánta energía genera un panel solar?',
        answer: 'Un panel típico de 450W genera aproximadamente 2.0-2.5 kWh por día en Paraguay (considerando 5 horas pico de sol). Esto equivale a alimentar un refrigerador durante 24 horas o un aire acondicionado durante 2-3 horas. El número de paneles necesarios depende de tu consumo mensual.'
      }
    ],
    financiamiento: [
      {
        question: '¿Cuánto cuesta un sistema solar?',
        answer: 'El costo depende del tamaño del sistema. Un sistema residencial típico de 3-5kW cuesta entre USD 4,000 y USD 7,000. Esto incluye paneles, inversor, estructura, instalación y trámites con ANDE. Usá nuestra calculadora solar para obtener un presupuesto exacto según tu consumo.'
      },
      {
        question: '¿Tienen opciones de financiamiento?',
        answer: 'Sí, ofrecemos múltiples opciones: Financiamiento propio con cuotas accesibles y Créditos con bancos aliados con plazos extendidos. Contamos con opciones con y sin intereses según la promoción vigente. La cuota mensual suele ser menor que el ahorro en electricidad, haciendo que el sistema se pague solo.'
      },
      {
        question: '¿Cuál es el retorno de inversión?',
        answer: 'El ROI típico es de 4-6 años para sistemas residenciales y 3-5 años para comerciales. Después de recuperar la inversión, disfrutás de energía prácticamente gratis por 20+ años.'
      },
      {
        question: '¿Qué documentos necesito para financiar?',
        answer: 'Para financiamiento propio: cédula de identidad, comprobante de ingresos y factura de electricidad. Para créditos bancarios se agregan: certificado de trabajo o RUC activo, estados de cuenta. El proceso es rápido y nosotros te guiamos en cada paso.'
      }
    ],
    mantenimiento: [
      {
        question: '¿Los paneles necesitan mantenimiento?',
        answer: 'Los paneles solares requieren muy poco mantenimiento. La lluvia naturalmente los limpia en la mayoría de los casos. Recomendamos una limpieza anual profesional, la cual realizamos nosotros como parte de nuestro servicio de mantenimiento. También hacemos una revisión técnica anual para asegurar el óptimo funcionamiento.'
      },
      {
        question: '¿Qué incluye el servicio de mantenimiento?',
        answer: 'Nuestro servicio de mantenimiento incluye: limpieza de paneles, inspección de conexiones eléctricas, verificación del inversor, análisis de producción, ajuste de estructuras si es necesario, y reporte detallado del estado del sistema. Ofrecemos planes anuales con descuento.'
      },
      {
        question: '¿Con qué frecuencia debo limpiar los paneles?',
        answer: 'Recomendamos una limpieza anual profesional, la cual realizamos nosotros como parte de nuestro servicio de mantenimiento. La limpieza se hace con agua y equipos especializados para garantizar el óptimo rendimiento de tus paneles sin dañarlos.'
      },
      {
        question: '¿Qué hago si mi sistema produce menos de lo esperado?',
        answer: 'Primero verificá si hay suciedad en los paneles o sombras nuevas (árboles que crecieron, construcciones cercanas). Revisá el monitoreo para identificar el problema. Contactanos para una evaluación gratuita. Puede ser algo simple como suciedad o un problema técnico que resolveremos bajo garantía.'
      },
      {
        question: '¿Los paneles se dañan con el granizo?',
        answer: 'Los paneles están diseñados para resistir granizo de hasta 25mm de diámetro a 80 km/h. En pruebas, soportan impactos equivalentes a pelotas de tenis a alta velocidad. En caso de daño por eventos climáticos extremos, la garantía y tu seguro de hogar pueden cubrir el reemplazo.'
      }
    ],
    garantias: [
      {
        question: '¿Qué garantías tienen los equipos?',
        answer: 'Paneles solares: 25 años de garantía de rendimiento (mínimo 80% de eficiencia) y 12 años de garantía de producto. Inversores: 5-10 años según marca (extendible). Estructuras: 10 años contra corrosión. Instalación: 5 años de garantía de mano de obra.'
      },
      {
        question: '¿Qué cubre la garantía de instalación?',
        answer: 'Nuestra garantía de instalación de 5 años cubre: defectos en el montaje de estructuras, problemas de cableado, filtraciones causadas por la instalación, fallas en conexiones. No cubre daños por eventos climáticos extremos, manipulación por terceros o falta de mantenimiento.'
      },
      {
        question: '¿Cómo hago un reclamo de garantía?',
        answer: 'Contactanos por WhatsApp, teléfono o email describiendo el problema. Nuestro equipo técnico evaluará si corresponde a garantía. Si es un problema cubierto, programamos la visita técnica sin costo. Tiempo de respuesta: 24-48 horas hábiles para evaluación, 5-10 días para resolución.'
      },
      {
        question: '¿La garantía se transfiere si vendo mi casa?',
        answer: 'Sí, la garantía de los equipos está asociada a la instalación, no al propietario. Si vendés tu casa, la garantía se transfiere al nuevo propietario. Solo necesitás notificarnos el cambio de titularidad para actualizar nuestros registros.'
      },
      {
        question: '¿Qué pasa si la empresa fabricante cierra?',
        answer: 'Trabajamos exclusivamente con marcas de primer nivel con presencia global y solidez financiera (Longi, JA Solar, Huawei, Growatt). Además, como instaladores certificados, Zenex respalda los equipos incluso si hubiera problemas con el fabricante.'
      }
    ]
  };

  const filteredFaqs = searchTerm
    ? Object.values(faqs).flat().filter(faq => 
        faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : faqs[activeCategory];

  const popularQuestions = [
    { question: '¿Cuánto puedo ahorrar?', category: 'general' },
    { question: '¿Cuánto cuesta?', category: 'financiamiento' },
    { question: '¿Tienen financiamiento?', category: 'financiamiento' },
    { question: '¿Cuánto demora la instalación?', category: 'instalacion' }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="faq-page">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-72 h-72 bg-emerald-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-emerald-400/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="flex items-center justify-center gap-2 text-emerald-400 mb-6">
            <HelpCircle className="w-5 h-5" />
            <span className="text-sm font-medium tracking-wider uppercase">Centro de Ayuda</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Preguntas Frecuentes
          </h1>
          
          <p className="text-xl text-zinc-300 mb-8">
            Encontrá respuestas a las preguntas más comunes sobre energía solar
          </p>
          
          {/* Search Bar */}
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400" />
            <input
              type="text"
              placeholder="Buscar preguntas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          {/* Popular Questions */}
          <div className="flex flex-wrap justify-center gap-2 mt-6">
            <span className="text-zinc-500 text-sm">Populares:</span>
            {popularQuestions.map((q, index) => (
              <button
                key={index}
                onClick={() => {
                  setSearchTerm(q.question);
                  setActiveCategory(q.category);
                }}
                className="px-3 py-1 text-sm bg-white/10 hover:bg-white/20 text-zinc-300 rounded-full transition-colors"
              >
                {q.question}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="sticky top-0 bg-white border-b border-zinc-200 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto py-4 gap-2 scrollbar-hide">
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => {
                    setActiveCategory(category.id);
                    setSearchTerm('');
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                    activeCategory === category.id && !searchTerm
                      ? 'bg-emerald-600 text-white'
                      : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {category.name}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ List */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {searchTerm && (
            <p className="text-zinc-500 mb-6">
              {filteredFaqs.length} resultado(s) para "{searchTerm}"
            </p>
          )}

          <div className="space-y-4">
            {filteredFaqs.map((faq, index) => (
              <div 
                key={index}
                className="border border-zinc-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-zinc-50 transition-colors"
                >
                  <span className="font-semibold text-zinc-900 pr-4">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-emerald-600 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 text-zinc-600 border-t border-zinc-100 pt-4">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>

          {filteredFaqs.length === 0 && (
            <div className="text-center py-12">
              <HelpCircle className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-zinc-900 mb-2">No encontramos resultados</h3>
              <p className="text-zinc-500 mb-4">Probá con otros términos o contactanos directamente</p>
              <a 
                href="https://wa.me/595991344956?text=Hola,%20tengo%20una%20consulta"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                <MessageCircle className="w-5 h-5" />
                Consultar por WhatsApp
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-16 bg-zinc-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-zinc-900 mb-4">
            ¿No encontraste lo que buscabas?
          </h2>
          <p className="text-zinc-600 mb-8">
            Nuestro equipo está listo para ayudarte con cualquier consulta
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://wa.me/595991344956"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              WhatsApp
            </a>
            <a 
              href="tel:+595991344956"
              className="flex items-center gap-2 px-6 py-3 bg-zinc-800 text-white rounded-lg hover:bg-zinc-900 transition-colors"
            >
              <Phone className="w-5 h-5" />
              Llamar
            </a>
            <button 
              onClick={() => navigate('/calculadora')}
              className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-emerald-600 text-emerald-600 rounded-lg hover:bg-emerald-50 transition-colors"
            >
              <Calculator className="w-5 h-5" />
              Calculadora Solar
            </button>
          </div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-zinc-900 mb-8 text-center">
            También te puede interesar
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link 
              to="/soporte/garantias"
              className="p-6 bg-zinc-50 rounded-xl hover:shadow-lg transition-all group"
            >
              <Shield className="w-10 h-10 text-emerald-600 mb-4" />
              <h3 className="font-bold text-zinc-900 mb-2 group-hover:text-emerald-600 transition-colors">Garantías</h3>
              <p className="text-zinc-600 text-sm">Conocé la cobertura y términos de garantía de nuestros equipos</p>
            </Link>
            <Link 
              to="/soporte/servicio-tecnico"
              className="p-6 bg-zinc-50 rounded-xl hover:shadow-lg transition-all group"
            >
              <Wrench className="w-10 h-10 text-emerald-600 mb-4" />
              <h3 className="font-bold text-zinc-900 mb-2 group-hover:text-emerald-600 transition-colors">Servicio Técnico</h3>
              <p className="text-zinc-600 text-sm">Mantenimiento, reparaciones y soporte técnico profesional</p>
            </Link>
            <Link 
              to="/calculadora"
              className="p-6 bg-zinc-50 rounded-xl hover:shadow-lg transition-all group"
            >
              <Calculator className="w-10 h-10 text-emerald-600 mb-4" />
              <h3 className="font-bold text-zinc-900 mb-2 group-hover:text-emerald-600 transition-colors">Calculadora Solar</h3>
              <p className="text-zinc-600 text-sm">Calculá cuánto podés ahorrar con energía solar</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default FAQPage;
